import SelectField from '@/components/selectField';
import TextField from '@/components/textField';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import { Box, Button, Stack, alpha, useTheme } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  questions: Record<string, any>;
  userResponses: Record<string, any>;
  maxHeight: number;
  onParsedRequirementsChange: (questionId: string, response: any) => void;
  onBack: () => void;
  onCancel: () => void;
  onNext: () => void;
};

const StepExternalApi = ({
  questions = {},
  userResponses = {},
  maxHeight,
  onParsedRequirementsChange,
  onBack,
  onCancel,
  onNext,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [containerRef, containerSize] = useElementSize();
  const [actionPanelRef, actionPanelSize] = useElementSize();

  const [errors, setErrors] = useState<any>({});

  const setExternalApiSettings = (settingName: string, value: any) => {
    onParsedRequirementsChange('externalApiSettings', {
      ...userResponses.parsedRequirements.externalApiSettings,
      [settingName]: value,
    });
  };

  const handleNext = () => {
    const errors: any = {};
    let hasError = false;
    if (!userResponses.parsedRequirements.externalApiSettings?.endpoint) {
      errors.externalApiEndpoint = t('common.errors.requiredField');
      hasError = true;
    }
    if (!userResponses.parsedRequirements.externalApiSettings?.authenticationType) {
      errors.externalApiAuthenticationType = t('common.errors.requiredField');
      hasError = true;
    }
    if (userResponses.parsedRequirements.externalApiSettings?.authenticationType === 'HttpBasic') {
      if (!userResponses.parsedRequirements.externalApiSettings?.username) {
        errors.externalApiHttpBasicUsername = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.parsedRequirements.externalApiSettings?.password) {
        errors.externalApiHttpBasicPassword = t('common.errors.requiredField');
        hasError = true;
      }
    }
    setErrors(errors);
    if (!hasError) {
      onNext();
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleBack = () => {
    onBack();
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        height: `${maxHeight}px`,
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderLeft: `3px solid ${theme.palette.secondary.main}`,
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        <Stack alignItems={'flex-start'} spacing={4}>
          <TextField
            label={questions.externalApiSettings.endpoint.question}
            value={userResponses.parsedRequirements.externalApiSettings?.endpoint || ''}
            error={errors.externalApiEndpoint}
            onChange={(value) => setExternalApiSettings('endpoint', value)}
          />
          <SelectField
            label={questions.externalApiSettings.authenticationType.question}
            value={userResponses.parsedRequirements.externalApiSettings?.authenticationType || ''}
            options={questions.externalApiSettings.authenticationType.options || []}
            required
            error={errors.externalApiAuthenticationType}
            onChange={(value) => setExternalApiSettings('authenticationType', value)}
            fullWidth={false}
          />
          {userResponses.parsedRequirements.externalApiSettings?.authenticationType === 'HttpBasic' && (
            <>
              <TextField
                label={questions.externalApiSettings.username.question}
                value={userResponses.parsedRequirements.externalApiSettings?.username || ''}
                error={errors.externalApiHttpBasicUsername}
                fullWidth={false}
                onChange={(value) => setExternalApiSettings('username', value)}
              />
              <TextField
                label={questions.externalApiSettings.password.question}
                value={userResponses.parsedRequirements.externalApiSettings?.password || ''}
                error={errors.externalApiHttpBasicPassword}
                fullWidth={false}
                onChange={(value) => setExternalApiSettings('password', value)}
                type="password"
              />
            </>
          )}
        </Stack>
      </Box>
      <Box
        ref={actionPanelRef}
        sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.02) }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
          >
            {t('common.buttons.back')}
          </Button>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="text"
              onClick={handleCancel}
              sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
            >
              {t('common.buttons.cancel')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '10px',
                px: 2.5,
                color: 'text.primary',
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              {t('common.buttons.next')}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default StepExternalApi;
